/** One row of post metrics, keyed by ss_post_id / permalink in the results map. */
export type PostResultStats = {
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  engagementRate?: number;
  caption?: string | null;
  hashtags?: string[] | string | null;
  [key: string]: unknown;
};

export interface HashtagComparison {
  prescribed: string[];
  onPost: string[];
  missing: string[];
  matched: string[];
}

export interface CtaComparison {
  prescribed: string;
  foundOnPost: boolean;
  caption: string | null;
}

/**
 * Super Social returns a flat id → stats map. Ultra Social wraps the same map in
 * `{ results, submittedPosts }`. Lookup always wants the flat map.
 */
export function unwrapLatestResults(payload: unknown): Record<string, PostResultStats> {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }

  const record = payload as Record<string, unknown>;
  if ('results' in record || 'submittedPosts' in record) {
    const nested = record['results'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, PostResultStats>;
    }
    return {};
  }

  return record as Record<string, PostResultStats>;
}

/** Split brief `suggestedHastags` the same way validation does: spaces, strip # / commas, skip @mentions. */
export function parsePrescribedHashtags(raw: string | null | undefined): string[] {
  if (!raw?.trim()) {
    return [];
  }

  return uniqueNormalized(
    raw
      .trim()
      .replaceAll(',', ' ')
      .split(/\s+/)
      .map((tag) => stripHashtagDecorators(tag))
      .filter((tag) => tag.length > 0 && tag.charAt(0) !== '@'),
  );
}

/** Prefer the Ultra `hashtags` field; fall back to `#tags` in the caption. */
export function parsePostHashtags(
  hashtags: string[] | string | null | undefined,
  caption?: string | null,
): string[] {
  const fromField = normalizeHashtagField(hashtags);
  if (fromField.length > 0) {
    return fromField;
  }
  return parseHashtagsFromCaption(caption);
}

export function compareHashtags(
  prescribedRaw: string | null | undefined,
  hashtags: string[] | string | null | undefined,
  caption?: string | null,
): HashtagComparison {
  const prescribed = parsePrescribedHashtags(prescribedRaw);
  const onPost = parsePostHashtags(hashtags, caption);
  const onPostSet = new Set(onPost.map((tag) => tag.toLowerCase()));
  const matched = prescribed.filter((tag) => onPostSet.has(tag.toLowerCase()));
  const missing = prescribed.filter((tag) => !onPostSet.has(tag.toLowerCase()));
  return { prescribed, onPost, missing, matched };
}

export function compareCta(
  prescribedRaw: string | null | undefined,
  caption?: string | null,
): CtaComparison | null {
  const prescribed = prescribedRaw?.trim() ?? '';
  if (!prescribed) {
    return null;
  }

  return {
    prescribed,
    foundOnPost: captionContainsCta(caption, prescribed),
    caption: caption?.trim() ? caption : null,
  };
}

export function displayHashtag(tag: string): string {
  const trimmed = tag.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function normalizeHashtagField(hashtags: string[] | string | null | undefined): string[] {
  if (Array.isArray(hashtags)) {
    return uniqueNormalized(hashtags.map((tag) => stripHashtagDecorators(String(tag))));
  }
  if (typeof hashtags === 'string' && hashtags.trim()) {
    return uniqueNormalized(
      hashtags
        .split(/[,\s]+/)
        .map((tag) => stripHashtagDecorators(tag))
        .filter(Boolean),
    );
  }
  return [];
}

function parseHashtagsFromCaption(caption: string | null | undefined): string[] {
  if (!caption) {
    return [];
  }
  const matches = caption.match(/#[\p{L}\p{N}_]+/gu) ?? [];
  return uniqueNormalized(matches.map((tag) => stripHashtagDecorators(tag)));
}

function captionContainsCta(caption: string | null | undefined, cta: string): boolean {
  if (!caption) {
    return false;
  }
  const haystack = caption.toLowerCase();
  const needle = cta.trim().replace(/\/+$/, '').toLowerCase();
  return haystack.includes(needle) || haystack.includes(cta.trim().toLowerCase());
}

function stripHashtagDecorators(tag: string): string {
  return tag.replaceAll('#', '').replaceAll(',', '').replaceAll(' ', '').trim();
}

function uniqueNormalized(tags: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const tag of tags) {
    if (!tag) {
      continue;
    }
    const key = tag.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    unique.push(tag);
  }
  return unique;
}
